/**
 * useContractSetupState — the page's ONE selector.
 *
 * It reads what is already cached (`useContractSetup` for the contract, its preparation, its
 * insurance, its deposits and its money; `useContractAgreements` for the signing slots) and hands
 * the whole lot to `computeSetupState` as one snapshot. The manifest rows, the narrator sentence,
 * the three progress numbers and the activate gate all come out of that single call, which is
 * what makes them provably consistent: there is no second derivation to disagree with.
 *
 * THE SHAPE-SHIFTING ITSELF LIVES IN `snapshotAdapter.ts`, which is React-free and unit-tested.
 * What is left here is the impure half and only that: which caches are read, when the clock
 * ticks, and how a lazily-arriving render finds its way into the snapshot.
 *
 * TWO INPUTS THE PAGE CANNOT HOLD, AND WHAT IS DONE ABOUT THEM:
 *
 * - `missing[]` per document comes from `['agreement-render', documentId]`, which is a LAZY,
 *   per-row cache entry and IS the shown-hash record — pulling it up to page level would fetch a
 *   render for documents nobody opened and would move the hash the countersignature attests to.
 *   So the snapshot carries it only when a row has already put it in the cache, and the witness
 *   lock falls back to the hire purchase agreement's own legacy `signed_at` (which is what the
 *   template's `contract.hire_purchase_signed_date` resolves from anyway, paper originals
 *   included).
 *
 *   READING IT IS NOT ENOUGH ON ITS OWN: the render arrives AFTER the snapshot is first built,
 *   and a value read through a ref cannot ask for a recomputation. It used to be exactly that —
 *   a ref, deliberately outside the memo's dependencies — so a row that had just fetched its
 *   pre-flight went on showing the pre-render answer until some unrelated dependency happened to
 *   move. The render is now held as STATE keyed by document id, kept up to date by a query-cache
 *   subscription that fetches nothing, and the map is a dependency of the snapshot like any
 *   other. The map's identity only changes when its contents do, so a settled page still
 *   recomputes never.
 * - the elapsed seal counter needs a clock. It ticks ONLY while a contract instrument is
 *   countersigned but unsealed, so a settled page re-renders never.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useContractAgreements } from '@/components/agreements/useContractAgreements';
import type { AgreementDocumentRow } from '@/lib/agreements/api';

import { useContractSetup } from './useContractSetup';
import {
    computeSetupState,
    resolveLandingArea,
    type SetupSnapshot,
    type SetupState,
} from './setupState';
import { buildSetupSnapshot, type ContractShape } from './snapshotAdapter';
import type { ContractDeposit } from './data/depositShapes';

/** How often the seal counter re-reads the clock while a finalize is genuinely outstanding. */
const SEAL_TICK_MS = 1_000;

/** The lazy per-row render cache, by document id. Empty until a row has opened one. */
type RenderMissingMap = Record<number, string[]>;

const NO_RENDERS: RenderMissingMap = {};

/** True while a contract instrument is signed by everyone and its executed PDF is not filed. */
function sealIsRunning(documents: AgreementDocumentRow[] | undefined): boolean {
    return (documents ?? []).some(
        (doc) =>
            (doc.doc_type === 'HIRE_PURCHASE' || doc.doc_type === 'WITNESS') &&
            doc.status === 'COMPLETED' &&
            !doc.finalized_at,
    );
}

function useTickingClock(active: boolean): number {
    const [tick, setTick] = useState(() => Date.now());
    useEffect(() => {
        if (!active) return;
        const timer = window.setInterval(() => setTick(Date.now()), SEAL_TICK_MS);
        return () => window.clearInterval(timer);
    }, [active]);
    return active ? tick : 0;
}

/**
 * Same map back unless something actually moved.
 *
 * The cache hands out the SAME `missing[]` array for the same entry until that entry is written
 * again, so reference equality is the change signal — and returning the previous object when
 * nothing moved is what keeps the snapshot memo from recomputing on every unrelated cache event.
 */
function sameRenders(previous: RenderMissingMap, next: RenderMissingMap): boolean {
    const previousIds = Object.keys(previous);
    if (previousIds.length !== Object.keys(next).length) return false;
    return previousIds.every((id) => previous[Number(id)] === next[Number(id)]);
}

export interface ContractSetupStateResult {
    state: SetupState;
    snapshot: SetupSnapshot;
    /** Every underlying read has answered. The landing redirect waits for this. */
    stateReady: boolean;
    /** Where a route-less entry should land. Meaningless until `stateReady`. */
    landingArea: ReturnType<typeof resolveLandingArea>;
    setup: ReturnType<typeof useContractSetup>;
    agreements: ReturnType<typeof useContractAgreements>;
}

interface Options {
    contractId: number;
    /** The last `sign_contract` refusal, verbatim, so the sentences land on their rows. */
    refusals?: string[];
    /** Unsaved local edits, by row id. */
    drafts?: SetupSnapshot['drafts'];
    onSuccess?: () => void;
    /** Reads `missing[]` out of the render cache for a document a row has already opened. */
    renderMissing?: (documentId: number) => string[] | null;
}

export function useContractSetupState({
    contractId,
    refusals,
    drafts,
    onSuccess,
    renderMissing,
}: Options): ContractSetupStateResult {
    const queryClient = useQueryClient();
    const setup = useContractSetup(contractId);
    const agreements = useContractAgreements({ contractId, onSuccess });

    const clock = useTickingClock(sealIsRunning(agreements.documents));

    const documentIds = useMemo(
        () =>
            agreements.slots
                .map((slot) => slot.document?.id)
                .filter((id): id is number => typeof id === 'number'),
        [agreements.slots],
    );

    /**
     * The renders this contract's rows have already fetched — READ, NEVER FETCHED.
     *
     * The subscription is to the cache itself rather than to a query: `['agreement-render', id]`
     * is a lazy per-row entry and mounting an observer for it here would issue the fetch this
     * page must not issue. A cache event only ever causes a re-read of ids this contract owns,
     * and `sameRenders` drops the update when nothing moved.
     */
    const [renders, setRenders] = useState<RenderMissingMap>(NO_RENDERS);

    useEffect(() => {
        if (!renderMissing) {
            setRenders((previous) => (previous === NO_RENDERS ? previous : NO_RENDERS));
            return;
        }
        const read = () => {
            const next: RenderMissingMap = {};
            for (const id of documentIds) {
                const missing = renderMissing(id);
                if (missing) next[id] = missing;
            }
            setRenders((previous) => (sameRenders(previous, next) ? previous : next));
        };
        read();
        return queryClient.getQueryCache().subscribe((event) => {
            if (event.query.queryKey?.[0] === 'agreement-render') read();
        });
    }, [queryClient, renderMissing, documentIds]);

    const snapshot = useMemo<SetupSnapshot>(
        () =>
            buildSetupSnapshot({
                contractId,
                status: setup.contractStatus ?? null,
                now: clock ? new Date(clock) : new Date(),
                prep: setup.prepStatus ?? [],
                contract: (setup.contract ?? null) as ContractShape | null,
                // NOT CAST. `ContractInsurance.loans` is typed from the generated schema now, so
                // handing it over unasserted is what makes the compiler check `interest_rate`
                // against `LoanShape` — the seam that read a `rate` that never existed.
                insurance: setup.insurance ?? null,
                instalment: setup.instalment ?? null,
                deposits: (setup.depositPayments ?? []) as ContractDeposit[],
                witness: agreements.witness,
                slots: agreements.slots,
                // A refused read still mints a full board, so whether it succeeded travels with
                // the slots [D-B57] — see `SetupSnapshot.agreementsUnread`.
                agreementsFailed: agreements.failed,
                tokens: agreements.tokens,
                renderMissing: (documentId) => renders[documentId] ?? null,
                drafts,
                refusals,
                // The server's readiness answer — the authority the gate rows, the unmet count and
                // the Activate control are all drawn from.
                setupStatus: setup.setupStatus,
            }),
        [
            contractId,
            clock,
            drafts,
            refusals,
            renders,
            setup.contract,
            setup.contractStatus,
            setup.setupStatus,
            setup.prepStatus,
            setup.insurance,
            setup.instalment,
            setup.depositPayments,
            agreements.witness,
            agreements.slots,
            agreements.failed,
            agreements.tokens,
        ],
    );

    const state = useMemo(() => computeSetupState(snapshot), [snapshot]);
    const stateReady = setup.stateReady && !agreements.loading;

    return {
        state,
        snapshot,
        stateReady,
        landingArea: resolveLandingArea(state),
        setup,
        agreements,
    };
}
